'use client'

import Link from 'next/link'
import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { PublicChatAppLabels } from '@/lib/free-santa'
import { PUBLIC_CHAT_LIMITS } from '@/server/domain/santa-chat/public/limits'
import { useTurnstile } from './use-turnstile'

interface ConsentPanelProps {
  labels: PublicChatAppLabels['consent']
  /** La apertura está en curso: el botón espera. */
  busy: boolean
  /** Lo que salió mal al abrir (los elfos): se dice bajo el botón. */
  error: string | null
  /** `turnstileToken` es la respuesta del reto anti-bot; `null` si no se pudo resolver. */
  onStart: (name: string | null, turnstileToken: string | null) => void
}

/**
 * La puerta del chat público: sin la casilla del adulto marcada no hay botón
 * (AC1.1), así que tampoco se pide ninguna sesión. El nombre es opcional.
 * Marcarla es también aceptar los términos y la privacidad, enlazados en la
 * propia casilla (en pestaña nueva: el formulario no se pierde).
 * Al pulsar «Empezar» se resuelve el reto anti-bot invisible (ADR-050) y su
 * respuesta viaja con la apertura.
 */
export function ConsentPanel({ labels, busy, error, onStart }: ConsentPanelProps) {
  const adultId = useId()
  const nameId = useId()
  const [adultConfirmed, setAdultConfirmed] = useState(false)
  const [name, setName] = useState('')
  const [solving, setSolving] = useState(false)
  const { containerRef: challengeRef, solve: solveChallenge } = useTurnstile()
  const waiting = busy || solving

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!adultConfirmed || waiting) return
    setSolving(true)
    const turnstileToken = await solveChallenge()
    setSolving(false)
    onStart(name.trim() || null, turnstileToken)
  }

  return (
    <form onSubmit={submit} className="readable-body mx-auto flex w-full max-w-md flex-col gap-5 p-6">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-christmas text-3xl font-bold">{labels.title}</h1>
        <p className="text-muted-foreground">{labels.body}</p>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={nameId}>{labels.nameLabel}</Label>
        <Input
          id={nameId}
          value={name}
          maxLength={PUBLIC_CHAT_LIMITS.maxNameLength}
          placeholder={labels.namePlaceholder}
          autoComplete="off"
          // 16 px: below that, Safari on iOS zooms in on focus and never zooms back.
          className="h-12 rounded-full px-4 text-base md:text-base"
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="flex items-start gap-3">
        <input
          id={adultId}
          type="checkbox"
          checked={adultConfirmed}
          className="mt-0.5 size-5 shrink-0 accent-primary"
          onChange={(event) => setAdultConfirmed(event.target.checked)}
        />
        <Label htmlFor={adultId} className="block leading-snug">
          {labels.adultConfirm.map((segment) =>
            segment.kind === 'text' ? (
              segment.text
            ) : (
              <Link
                key={segment.link}
                href={labels.links[segment.link].href}
                target="_blank"
                rel="noopener"
                className="underline underline-offset-2 hover:text-primary"
              >
                {labels.links[segment.link].label}
              </Link>
            ),
          )}
        </Label>
      </div>
      <div ref={challengeRef} />
      <Button type="submit" size="lg" className="min-h-12 rounded-full text-base" disabled={!adultConfirmed || waiting}>
        {labels.start}
      </Button>
      {error && (
        <p role="alert" className="text-center text-sm text-destructive">
          {error}
        </p>
      )}
      <p className="text-center text-xs text-muted-foreground">{labels.note}</p>
    </form>
  )
}
